/**
 * API Client with authentication interceptor.
 * Deploy trigger: 2024-12-29-v2-final-prefix-fix
 *
 * This is the foundation for all API calls in the frontend.
 * Features:
 * - Automatic auth header injection
 * - Request/response interceptors
 * - Error handling with proper typing
 * - Retry logic for transient failures
 * - Request cancellation support
 */

import { ApiError, isApiError, type ApiErrorResponse } from './types';

// ============================================================
// Configuration
// ============================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const DEFAULT_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 30000; // default 30 seconds
const RETRY_DELAY = Number(import.meta.env.VITE_API_RETRY_DELAY) || 1000; // default 1 second

// ============================================================
// Types
// ============================================================

export interface RequestConfig extends Omit<RequestInit, 'body'> {
  body?: BodyInit | Record<string, unknown> | null;
  timeout?: number;
  retries?: number;
  skipAuth?: boolean;
  params?: Record<string, QueryParamValue>;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

type AuthTokenGetter = () => string | null;
type AuthRefresher = () => Promise<string | null>;
type QueryParamPrimitive = string | number | boolean;
export type QueryParamValue =
  | QueryParamPrimitive
  | readonly QueryParamPrimitive[]
  | undefined;

// ============================================================
// Client State
// ============================================================

let authTokenGetter: AuthTokenGetter = () => null;
let authRefresher: AuthRefresher | null = null;
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Request interceptors
type RequestInterceptor = (config: RequestConfig) => RequestConfig | Promise<RequestConfig>;
type ResponseInterceptor = <T>(response: ApiResponse<T>) => ApiResponse<T> | Promise<ApiResponse<T>>;
type ErrorInterceptor = (error: ApiError) => ApiError | Promise<never>;

const requestInterceptors: RequestInterceptor[] = [];
const responseInterceptors: ResponseInterceptor[] = [];
const errorInterceptors: ErrorInterceptor[] = [];

// ============================================================
// Helper Functions
// ============================================================

function appendQueryParam(searchParams: URLSearchParams, key: string, value: QueryParamValue): void {
  if (value === undefined) return;
  if (Array.isArray(value)) {
    value.forEach((item) => searchParams.append(key, String(item)));
    return;
  }
  searchParams.append(key, String(value));
}

function buildUrl(endpoint: string, params?: Record<string, QueryParamValue>): string {
  const fullPath = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
  // Note: redirect_slashes=False in FastAPI backend - no trailing slash needed
  
  // For absolute URLs (http://...), return as-is with params
  // For relative URLs (/api/...), keep them relative so Vite proxy works
  const isAbsoluteUrl = fullPath.startsWith('http');
  
  if (isAbsoluteUrl) {
    const url = new URL(fullPath);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        appendQueryParam(url.searchParams, key, value);
      });
    }
    return url.toString();
  }
  
  // For relative URLs, build query string manually to preserve relativity
  let queryString = '';
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      appendQueryParam(searchParams, key, value);
    });
    const paramStr = searchParams.toString();
    if (paramStr) {
      queryString = `?${paramStr}`;
    }
  }
  
  return `${fullPath}${queryString}`;
}

function createAbortController(timeout: number): { controller: AbortController; timeoutId: NodeJS.Timeout } {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  return { controller, timeoutId };
}

async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type');

  if (contentType?.includes('application/json')) {
    return response.json();
  }

  if (contentType?.includes('text/')) {
    return response.text() as unknown as T;
  }

  // For binary data, return blob
  return response.blob() as unknown as T;
}

async function handleError(response: Response, _attempt: number, _maxRetries: number): Promise<never> {
  let errorData: ApiErrorResponse | null = null;

  try {
    errorData = await response.json();
  } catch {
    // Response might not be JSON
  }

  const error = new ApiError(
    errorData?.detail || errorData?.message || `Request failed with status ${response.status}`,
    response.status,
    errorData
  );

  // Run error interceptors
  for (const interceptor of errorInterceptors) {
    await interceptor(error);
  }

  throw error;
}

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ============================================================
// Core Request Function
// ============================================================

async function request<T>(
  method: string,
  endpoint: string,
  config: RequestConfig = {}
): Promise<ApiResponse<T>> {
  const {
    timeout = DEFAULT_TIMEOUT,
    retries = 0,
    skipAuth = false,
    params,
    headers: customHeaders,
    body,
    ...restConfig
  } = config;

  // Build request config through interceptors
  let finalConfig: RequestConfig = {
    method,
    headers: customHeaders,
    body,
    ...restConfig,
  };

  for (const interceptor of requestInterceptors) {
    finalConfig = await interceptor(finalConfig);
  }

  // Build headers
  const headers = new Headers(finalConfig.headers);

  // Add auth header if not skipped
  if (!skipAuth) {
    const token = authTokenGetter();
    // Debug auth log removed for production
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    } else {
      // Removed console.log
    }
  }

  // Add content-type for JSON bodies
  if (body && typeof body === 'object' && !(body instanceof FormData) && !(body instanceof Blob)) {
    headers.set('Content-Type', 'application/json');
  }

  const url = buildUrl(endpoint, params);
  const { controller, timeoutId } = createAbortController(timeout);

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, {
        ...finalConfig,
        method,
        credentials: finalConfig.credentials ?? 'include',
        headers,
        body: body instanceof FormData || body instanceof Blob || typeof body === 'string'
          ? body
          : body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle 401 - try to refresh token
      if (response.status === 401 && authRefresher && !skipAuth) {
        const newToken = await refreshAuthToken();
        if (newToken !== null) {
          if (newToken) {
            headers.set('Authorization', `Bearer ${newToken}`);
          } else {
            headers.delete('Authorization');
          }
          // Retry request with new token
          const retryResponse = await fetch(url, {
            ...finalConfig,
            method,
            credentials: finalConfig.credentials ?? 'include',
            headers,
            body: body instanceof FormData || body instanceof Blob || typeof body === 'string'
              ? body
              : body ? JSON.stringify(body) : undefined,
          });

          if (!retryResponse.ok) {
            await handleError(retryResponse, attempt, retries);
          }

          const data = await parseResponse<T>(retryResponse);
          let result: ApiResponse<T> = { data, status: retryResponse.status, headers: retryResponse.headers };

          for (const interceptor of responseInterceptors) {
            result = await interceptor(result);
          }

          return result;
        }
      }

      if (!response.ok) {
        // Retry on 5xx errors
        if (response.status >= 500 && attempt < retries) {
          lastError = new ApiError(`Server error: ${response.status}`, response.status);
          await sleep(RETRY_DELAY * Math.pow(2, attempt)); // Exponential backoff
          continue;
        }

        await handleError(response, attempt, retries);
      }

      const data = await parseResponse<T>(response);
      let result: ApiResponse<T> = { data, status: response.status, headers: response.headers };

      // Run response interceptors
      for (const interceptor of responseInterceptors) {
        result = await interceptor(result);
      }

      return result;

    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiError('Request timeout', 408);
      }

      if (isApiError(error)) {
        throw error;
      }

      lastError = error instanceof Error ? error : new Error(String(error));

      // Retry on network errors
      if (attempt < retries) {
        await sleep(RETRY_DELAY * Math.pow(2, attempt));
        continue;
      }
    }
  }

  throw lastError || new ApiError('Request failed', 0);
}

async function refreshAuthToken(): Promise<string | null> {
  if (!authRefresher) return null;

  // Dedupe concurrent refresh requests
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = authRefresher();

  try {
    const token = await refreshPromise;
    return token;
  } finally {
    isRefreshing = false;
    refreshPromise = null;
  }
}

// ============================================================
// Public API
// ============================================================

export const apiClient = {
  /**
   * Configure authentication
   */
  setAuthTokenGetter(getter: AuthTokenGetter) {
    authTokenGetter = getter;
  },

  setAuthRefresher(refresher: AuthRefresher) {
    authRefresher = refresher;
  },

  /**
   * Add interceptors
   */
  addRequestInterceptor(interceptor: RequestInterceptor) {
    requestInterceptors.push(interceptor);
    return () => {
      const index = requestInterceptors.indexOf(interceptor);
      if (index > -1) requestInterceptors.splice(index, 1);
    };
  },

  addResponseInterceptor(interceptor: ResponseInterceptor) {
    responseInterceptors.push(interceptor);
    return () => {
      const index = responseInterceptors.indexOf(interceptor);
      if (index > -1) responseInterceptors.splice(index, 1);
    };
  },

  addErrorInterceptor(interceptor: ErrorInterceptor) {
    errorInterceptors.push(interceptor);
    return () => {
      const index = errorInterceptors.indexOf(interceptor);
      if (index > -1) errorInterceptors.splice(index, 1);
    };
  },

  /**
   * HTTP Methods
   */
  get<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return request<T>('GET', endpoint, config);
  },

  post<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
    return request<T>('POST', endpoint, { ...config, body: data as BodyInit });
  },

  put<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
    return request<T>('PUT', endpoint, { ...config, body: data as BodyInit });
  },

  patch<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
    return request<T>('PATCH', endpoint, { ...config, body: data as BodyInit });
  },

  delete<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return request<T>('DELETE', endpoint, config);
  },

  /**
   * Get binary data (for file downloads)
   */
  async getBlob(endpoint: string, config?: RequestConfig): Promise<Blob> {
    const url = buildUrl(endpoint, config?.params);
    const headers: Record<string, string> = {};

    if (!config?.skipAuth) {
      const token = authTokenGetter();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const response = await fetch(url, {
      method: 'GET',
      credentials: config?.credentials ?? 'include',
      headers,
      signal: config?.signal,
    });

    if (!response.ok) {
      throw new ApiError(`Download failed: ${response.status}`, response.status);
    }

    return response.blob();
  },

  /**
   * Upload files with progress tracking
   */
  async upload<T>(
    endpoint: string,
    files: File | File[] | FormData,
    onProgress?: (progress: number) => void,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    const formData = files instanceof FormData ? files : new FormData();

    if (!(files instanceof FormData)) {
      const fileArray = Array.isArray(files) ? files : [files];
      fileArray.forEach((file, index) => {
        formData.append(`file${index}`, file);
      });
    }

    // Use XMLHttpRequest for progress tracking
    if (onProgress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const url = buildUrl(endpoint, config?.params);

        // Support abort signal
        if (config?.signal) {
          config.signal.addEventListener('abort', () => {
            xhr.abort();
            reject(new ApiError('Upload aborted', 0));
          });
        }

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            onProgress((event.loaded / event.total) * 100);
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const data = JSON.parse(xhr.responseText);
            resolve({ data, status: xhr.status, headers: new Headers() });
          } else {
            reject(new ApiError(`Upload failed: ${xhr.status}`, xhr.status));
          }
        };

        xhr.onerror = () => {
          reject(new ApiError('Upload failed', 0));
        };

        xhr.onabort = () => {
          reject(new ApiError('Upload aborted', 0));
        };

        xhr.open('POST', url);
        xhr.withCredentials = config?.credentials !== 'omit';

        const token = authTokenGetter();
        if (token && !config?.skipAuth) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        xhr.send(formData);
      });
    }

    return request<T>('POST', endpoint, {
      ...config,
      body: formData,
    });
  },

  /**
   * Raw fetch that returns the native Response object.
   *
   * Use this for streaming (ReadableStream / SSE via fetch) where you need
   * `response.body.getReader()`.  It still handles URL building, auth
   * injection, and base-URL resolution so callers don't duplicate that logic.
   */
  async fetchRaw(
    endpoint: string,
    config: RequestConfig & { method?: string } = {}
  ): Promise<Response> {
    const {
      timeout = DEFAULT_TIMEOUT,
      skipAuth = false,
      params,
      headers: customHeaders,
      body,
      method = 'GET',
      ...restConfig
    } = config;

    const headers = new Headers(customHeaders);

    if (!skipAuth) {
      const token = authTokenGetter();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    if (body && typeof body === 'object' && !(body instanceof FormData) && !(body instanceof Blob)) {
      headers.set('Content-Type', 'application/json');
    }

    const url = buildUrl(endpoint, params);
    const { controller, timeoutId } = createAbortController(timeout);

    const response = await fetch(url, {
      ...restConfig,
      method,
      credentials: restConfig.credentials ?? 'include',
      headers,
      body: body instanceof FormData || body instanceof Blob || typeof body === 'string'
        ? body
        : body ? JSON.stringify(body) : undefined,
      signal: config.signal ?? controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      await handleError(response, 0, 0);
    }

    return response;
  },

  /**
   * Server-Sent Events (SSE) connection
   * 
   * Includes auth error detection to prevent infinite reconnect loops on 401/403.
   */
  sse(endpoint: string, handlers: {
    onMessage: (event: MessageEvent) => void;
    onError?: (error: Event) => void;
    onOpen?: (event: Event) => void;
    onAuthError?: () => void;
  }): EventSource {
    const url = buildUrl(endpoint);
    const eventSource = new EventSource(url, { withCredentials: true });
    let openedAt = 0;

    eventSource.onmessage = handlers.onMessage;
    eventSource.onopen = (event: Event) => {
      openedAt = Date.now();
      handlers.onOpen?.(event);
    };

    // Auth error detection: EventSource doesn't expose HTTP status codes.
    // Auth errors (401/403) cause immediate CLOSED state before onopen fires.
    // Transient network errors also cause CLOSED, but after the connection
    // was open. We use timing to distinguish: CLOSED within 1s of creation
    // (or before onopen) is likely auth; later failures are transient.
    eventSource.onerror = (error: Event) => {
      const timeSinceOpen = Date.now() - openedAt;
      const isLikelyAuthError = eventSource.readyState === EventSource.CLOSED &&
                                 (openedAt === 0 || timeSinceOpen < 1000);

      if (isLikelyAuthError) {
        console.error('[SSE] Likely authentication error - closing connection');
        eventSource.close();
        handlers.onAuthError?.();
        return;
      }

      // Transient error - let caller handle reconnection
      handlers.onError?.(error);
    };

    return eventSource;
  },
};

export default apiClient;
